/**
 * "Filter sources" (the prototype's "Filter items" dialog for Sources): source types and status, several
 * of each at once; Clear and Apply. The sort lives in the list's own bar (one click away).
 */

import { useState } from 'react';
import type { SourceKind } from '@digester/core';
import { DialogFrame, registerDialog, type DialogProps } from '../../app/dialogs.js';
import { useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { SOURCE_KIND_LABELS } from '../../ui/source-icons.js';
import { Toggle } from '../../ui/form.js';
import { sourceList, type SourceStatusFilter } from './list-model.js';

const STATUS_OPTIONS: ReadonlyArray<readonly [SourceStatusFilter, string, string]> = [
  ['enabled', 'Enabled', 'Pulled on schedule'],
  ['paused', 'Paused', 'Switched off by you or by the plan'],
  ['failing', 'Failing', 'The last pull on this device failed, or a password is missing'],
];

const toggleIn = <T,>(list: readonly T[], value: T, on: boolean): T[] => (on ? [...new Set([...list, value])] : list.filter((v) => v !== value));

export function SourcesFilterDialog({ close, dialogId }: DialogProps) {
  const sources = useEngineQuery(qk.sources(), (e) => e.sources.list()).data ?? [];
  const initial = sourceList.get();
  const [kinds, setKinds] = useState<SourceKind[]>(initial.kinds);
  const [statuses, setStatuses] = useState<SourceStatusFilter[]>(initial.statuses);
  // The kinds the person has, with how many of each (a selected kind stays even if none is left).
  const counts = new Map<SourceKind, number>();
  for (const s of sources) counts.set(s.kind, (counts.get(s.kind) ?? 0) + 1);
  const kindsShown = (Object.keys(SOURCE_KIND_LABELS) as SourceKind[]).filter((k) => counts.has(k) || kinds.includes(k));
  return (
    <DialogFrame title="Filter sources" className="small-dialog" onClose={close} dialogId={dialogId}>
      <form
        id="filter-form"
        className="sources-filter"
        onSubmit={(e) => {
          e.preventDefault();
          sourceList.set({ kinds, statuses });
          close();
        }}
      >
        <h3 className="sources-filter-heading">Source type</h3>
        {kindsShown.map((k) => (
          <Toggle key={k} label={`${SOURCE_KIND_LABELS[k]} (${counts.get(k) ?? 0})`} name={`kind-${k}`} checked={kinds.includes(k)} onChange={(on) => setKinds((v) => toggleIn(v, k, on))} />
        ))}
        <h3 className="sources-filter-heading">Status</h3>
        {STATUS_OPTIONS.map(([value, label, help]) => (
          <Toggle key={value} label={label} help={help} name={`status-${value}`} checked={statuses.includes(value)} onChange={(on) => setStatuses((v) => toggleIn(v, value, on))} />
        ))}
        <p className="field-help">No type or no status ticked = all. Several ticked = any of them.</p>
        <div className="dialog-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setKinds([]);
              setStatuses([]);
            }}
          >
            Clear
          </button>
          <button type="submit" className="primary-button">
            Apply
          </button>
        </div>
      </form>
    </DialogFrame>
  );
}

let registered = false;
export function registerSourcesDialogs(): void {
  if (registered) return;
  registered = true;
  registerDialog('sources-filter', SourcesFilterDialog);
}
